import { useState, useEffect, useCallback, useRef, useMemo } from "react"
import { format, subMonths, addMonths, parseISO } from "date-fns"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import AppLayout from "../../components/layout/AppLayout"
import LineChart from "../../components/analytics/LineChart"
import FunnelFlowChart from "../../components/analytics/FunnelFlowChart"
import { api } from "../../lib/api"
import type { ApiError } from "../../lib/api"
import { useSelectedAccount } from "../../contexts/AccountContext"
import PlanLockNotice from "../../components/account/PlanLockNotice"
import AccountSelectionNotice from "../../components/account/AccountSelectionNotice"
import { PAGE_TITLES } from "../../lib/navItems"
import { readAiReportNav, writeAiReportNav } from "../../lib/aiReportNav"
import { getMockAiReport } from "../../lib/aiReportMock"
import { formatMonthLabel, calcMinMonth } from "../../lib/month"
import type { AiReportListItem, AiReportDetail, AiReportContent, AiReportType, TopPost, SelfTopPost } from "../../types/analytics"
import type { CompetitorSummary } from "../../types/analytics"
import type { AmbassadorRankingItem, TopUgcPost, UgcComparisonSide } from "../../types/analytics/fan-user"
import "../../styles/analytics.scss"

const POLLING_INTERVAL_MS = 5000
const MAX_POLL_COUNT = 36

const REPORT_TYPE_OPTIONS: { key: AiReportType; label: string }[] = [
  { key: "competitor", label: "競合分析" },
  { key: "funnel",     label: "ファネル分析" },
  { key: "fan_user",   label: "ファンユーザー分析" },
]

// "YYYY-MM-01" 形式の月文字列を扱うローカルヘルパー（date-fns 直呼び出し・toISOString は使わない）
const computeCurrentMonth = (): string => format(new Date(), "yyyy-MM-01")
const computePrevMonth = (): string => format(subMonths(new Date(), 1), "yyyy-MM-01")
const shiftMonth = (isoMonth: string, diff: number): string =>
  format(addMonths(parseISO(isoMonth), diff), "yyyy-MM-01")

const AiReportPage = () => {
  const { selectedAccountId, currentPermission, selectedAccount, setSingleAccountOnly } = useSelectedAccount()
  const canEdit = currentPermission === "edit"
  const locked = selectedAccount != null && !selectedAccount.features.ai

  useEffect(() => {
    setSingleAccountOnly(true)
  }, [setSingleAccountOnly])

  const [activeType, setActiveType] = useState<AiReportType>("competitor")
  const [selectedMonth, setSelectedMonth] = useState<string>(computePrevMonth())
  const [competitors, setCompetitors] = useState<CompetitorSummary[]>([])
  const [selectedCompetitorId, setSelectedCompetitorId] = useState<number | null>(null)
  // month -> AiReportListItem (non-competitor tabs)
  const [nonCompReportsByMonth, setNonCompReportsByMonth] = useState<Record<string, AiReportListItem>>({})
  // competitorId -> (month -> AiReportListItem)
  const [compReportsByMonth, setCompReportsByMonth] = useState<Record<number, Record<string, AiReportListItem>>>({})
  const [selectedReport, setSelectedReport] = useState<AiReportDetail | null>(null)

  const [loading, setLoading] = useState(true)
  const [competitorsLoading, setCompetitorsLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const pollCountRef = useRef(0)
  const isInitializedRef = useRef(false)

  const currentMonth = computeCurrentMonth()
  // 当月は生成不可（翌月にならないと生成できない）。当月選択時はサンプル表示にする
  const isCurrentMonth = selectedMonth === currentMonth

  // サンプル表示用モックは毎レンダー再構築せず memo 化する
  const sampleReport = useMemo(
    () => getMockAiReport(activeType, selectedMonth),
    [activeType, selectedMonth]
  )

  // ─── 月の範囲計算 ──────────────────────────────────────────

  const accountMinMonth = selectedAccount?.created_at
    ? calcMinMonth(selectedAccount.created_at)
    : "2020-01-01"

  const competitorMinMonth = useCallback((): string => {
    if (!selectedCompetitorId) return accountMinMonth
    const comp = competitors.find(c => c.id === selectedCompetitorId)
    if (!comp?.created_at) return accountMinMonth
    const compMin = calcMinMonth(comp.created_at)
    return compMin > accountMinMonth ? compMin : accountMinMonth
  }, [selectedCompetitorId, competitors, accountMinMonth])

  const minMonth = activeType === "competitor" ? competitorMinMonth() : accountMinMonth

  const canGoPrev = selectedMonth > minMonth
  // 当月まで選択可能（当月はサンプル表示）
  const canGoNext = selectedMonth < currentMonth

  // ─── ポーリング ─────────────────────────────────────────────

  const clearPoll = () => {
    if (pollTimerRef.current) { clearTimeout(pollTimerRef.current); pollTimerRef.current = null }
    pollCountRef.current = 0
  }

  // ─── データ取得 ──────────────────────────────────────────────

  const fetchCompetitors = useCallback(async (): Promise<CompetitorSummary[]> => {
    if (!selectedAccountId) return []
    setCompetitorsLoading(true)
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      const res = await api.get<CompetitorSummary[]>(`/analytics/competitors?${params}`)
      setCompetitors(res)
      return res
    } catch {
      return []
    } finally {
      setCompetitorsLoading(false)
    }
  }, [selectedAccountId])

  const fetchNonCompetitorReports = useCallback(async () => {
    if (!selectedAccountId) return
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      const res = await api.get<AiReportListItem[]>(`/analytics/ai_reports?${params}`)
      const byMonth: Record<string, AiReportListItem> = {}
      res.filter(r => r.report_type !== "competitor").forEach(r => {
        if (r.report_type === "funnel" || r.report_type === "fan_user") {
          byMonth[`${r.report_type}:${r.target_month}`] = r
        }
      })
      setNonCompReportsByMonth(byMonth)
      return res
    } catch {
      return []
    }
  }, [selectedAccountId])

  const fetchCompetitorReports = useCallback(async (competitorId: number) => {
    if (!selectedAccountId) return
    try {
      const params = new URLSearchParams({
        instagram_account_id: String(selectedAccountId),
        competitor_id:        String(competitorId)
      })
      const res = await api.get<AiReportListItem[]>(`/analytics/ai_reports?${params}`)
      const byMonth: Record<string, AiReportListItem> = {}
      res.forEach(r => { byMonth[r.target_month] = r })
      setCompReportsByMonth(prev => ({ ...prev, [competitorId]: byMonth }))
      return byMonth
    } catch {
      return {}
    }
  }, [selectedAccountId])

  const ensureCompetitorReport = useCallback(async (competitorId: number, month: string): Promise<AiReportListItem | null> => {
    if (!selectedAccountId) return null
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      const res = await api.post<AiReportListItem>(`/analytics/ai_reports?${params}`, {
        ai_report: { competitor_id: competitorId, target_month: month }
      })
      setCompReportsByMonth(prev => ({
        ...prev,
        [competitorId]: { ...(prev[competitorId] ?? {}), [month]: res }
      }))
      return res
    } catch (err) {
      // completed 済み等の 422（作成不可）は一覧を取り直して既存レポートにフォールバックする
      if ((err as ApiError).status === 422) {
        const byMonth = (await fetchCompetitorReports(competitorId)) as Record<string, AiReportListItem> | undefined
        return byMonth?.[month] ?? null
      }
      return null
    }
  }, [selectedAccountId, fetchCompetitorReports])

  const fetchDetail = useCallback(async (reportId: number) => {
    if (!selectedAccountId) return
    setDetailLoading(true)
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      const res = await api.get<AiReportDetail>(`/analytics/ai_reports/${reportId}?${params}`)
      setSelectedReport(res)
    } catch {
    } finally {
      setDetailLoading(false)
    }
  }, [selectedAccountId])

  // ─── 現在選択中レポートを取得 ──────────────────────────────

  const currentReport = (): AiReportListItem | null => {
    if (activeType === "competitor") {
      if (!selectedCompetitorId) return null
      return compReportsByMonth[selectedCompetitorId]?.[selectedMonth] ?? null
    }
    return nonCompReportsByMonth[`${activeType}:${selectedMonth}`] ?? null
  }

  // ─── ポーリング ─────────────────────────────────────────────

  const startPolling = useCallback((reportId: number, competitorId: number | null) => {
    clearPoll()
    const poll = async () => {
      if (pollCountRef.current >= MAX_POLL_COUNT) { clearPoll(); return }
      pollCountRef.current += 1

      let updated: AiReportListItem | null = null
      if (competitorId) {
        const byMonth = await fetchCompetitorReports(competitorId)
        updated = (byMonth as Record<string, AiReportListItem>)[selectedMonth] ?? null
      } else {
        const res = await fetchNonCompetitorReports()
        if (res) {
          updated = (res as AiReportListItem[]).find(r => r.id === reportId) ?? null
        }
      }

      if (!updated || updated.status === "generating" || updated.status === "pending") {
        pollTimerRef.current = setTimeout(poll, POLLING_INTERVAL_MS)
      } else {
        clearPoll()
        if (updated.status === "completed") await fetchDetail(reportId)
      }
    }
    pollTimerRef.current = setTimeout(poll, POLLING_INTERVAL_MS)
  }, [fetchCompetitorReports, fetchNonCompetitorReports, fetchDetail, selectedMonth])

  // ─── sessionStorage 保存 ──────────────────────────────────────
  // init 完了後のみ保存（初回レンダリングのデフォルト値で上書きするのを防ぐ）

  useEffect(() => {
    if (!selectedAccountId || !isInitializedRef.current) return
    if (activeType === "dashboard") return
    writeAiReportNav(selectedAccountId, { activeType, selectedMonth, selectedCompetitorId })
  }, [selectedAccountId, activeType, selectedMonth, selectedCompetitorId])

  // ─── 初期化 ──────────────────────────────────────────────────

  useEffect(() => {
    if (!selectedAccountId) { setLoading(false); return }
    const init = async () => {
      isInitializedRef.current = false
      setLoading(true)

      // sessionStorage から復元
      let savedType: AiReportType = "competitor"
      let savedMonth: string = computePrevMonth()
      let savedCompetitorId: number | null = null
      const saved = readAiReportNav(selectedAccountId)
      if (saved) {
        if (saved.activeType) savedType = saved.activeType as AiReportType
        if (saved.selectedMonth) savedMonth = saved.selectedMonth
        if (saved.selectedCompetitorId) savedCompetitorId = Number(saved.selectedCompetitorId)
      }

      setActiveType(savedType)
      setSelectedMonth(savedMonth)
      setSelectedReport(null)
      clearPoll()

      const [nonCompRes, fetchedCompetitors] = await Promise.all([
        fetchNonCompetitorReports(),
        fetchCompetitors(),
      ])

      // 全競合レポートを compReportsByMonth に展開（月1回制限の判定に使用）
      const allCompReports = (nonCompRes as AiReportListItem[] ?? [])
        .filter(r => r.report_type === "competitor" && r.competitor_id != null)
      if (allCompReports.length > 0) {
        const byCompetitor: Record<number, Record<string, AiReportListItem>> = {}
        allCompReports.forEach(r => {
          const cid = r.competitor_id!
          byCompetitor[cid] = { ...(byCompetitor[cid] ?? {}), [r.target_month]: r }
        })
        setCompReportsByMonth(byCompetitor)
      }

      // 当月はサンプル表示のため API を呼ばない
      const savedIsCurrentMonth = savedMonth === computeCurrentMonth()

      // 競合タブ: 保存済み競合を復元
      if (savedType === "competitor" && savedCompetitorId) {
        const exists = fetchedCompetitors.some(c => c.id === savedCompetitorId)
        if (exists) {
          setSelectedCompetitorId(savedCompetitorId)
          if (!savedIsCurrentMonth) {
            const byMonth = (await fetchCompetitorReports(savedCompetitorId)) as Record<string, AiReportListItem> | undefined
            const existing = byMonth?.[savedMonth]
            if (existing) {
              if (existing.status === "completed") await fetchDetail(existing.id)
            } else {
              const report = await ensureCompetitorReport(savedCompetitorId, savedMonth)
              if (report?.status === "completed") await fetchDetail(report.id)
            }
          }
        }
      } else if (savedType !== "competitor" && !savedIsCurrentMonth) {
        // 非競合タブ: 保存済み月のレポートを表示
        const report = (nonCompRes as AiReportListItem[] ?? [])
          .find(r => r.report_type === savedType && r.target_month === savedMonth)
        if (report?.status === "completed") await fetchDetail(report.id)
      }

      setLoading(false)
      isInitializedRef.current = true
    }
    init()
    return () => clearPoll()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAccountId])

  // ─── タブ切り替え ──────────────────────────────────────────

  const handleTabChange = async (type: AiReportType) => {
    setActiveType(type)
    setSelectedReport(null)
    const month = computePrevMonth()
    setSelectedMonth(month)
    clearPoll()
    if (type !== "competitor") {
      setSelectedCompetitorId(null)
      const key = `${type}:${month}`
      const report = nonCompReportsByMonth[key]
      if (report?.status === "completed") await fetchDetail(report.id)
      else if (report?.status === "generating") startPolling(report.id, null)
    }
  }

  // ─── 月変更 ─────────────────────────────────────────────────

  const handleMonthChange = async (month: string) => {
    setSelectedMonth(month)
    setSelectedReport(null)
    clearPoll()

    // 当月はサンプル表示。API もポーリングも走らせない
    if (month === currentMonth) return

    if (activeType === "competitor" && selectedCompetitorId) {
      const existing = compReportsByMonth[selectedCompetitorId]?.[month]
      if (!existing) {
        const report = await ensureCompetitorReport(selectedCompetitorId, month)
        if (report?.status === "completed") await fetchDetail(report.id)
        else if (report?.status === "generating") startPolling(report.id, selectedCompetitorId)
      } else {
        if (existing.status === "completed") await fetchDetail(existing.id)
        else if (existing.status === "generating") startPolling(existing.id, selectedCompetitorId)
      }
    } else if (activeType !== "competitor") {
      const key = `${activeType}:${month}`
      const report = nonCompReportsByMonth[key]
      if (report?.status === "completed") await fetchDetail(report.id)
      else if (report?.status === "generating") startPolling(report.id, null)
    }
  }

  // ─── 競合選択 ────────────────────────────────────────────────

  const handleCompetitorChange = async (competitorId: number | null) => {
    setSelectedCompetitorId(competitorId)
    setSelectedReport(null)
    clearPoll()
    if (!competitorId) return
    // 当月はサンプル表示のため API を呼ばない
    if (selectedMonth === currentMonth) return

    let byMonth = compReportsByMonth[competitorId]
    if (!byMonth) {
      byMonth = (await fetchCompetitorReports(competitorId)) as Record<string, AiReportListItem>
    }

    const existing = byMonth?.[selectedMonth]
    if (!existing) {
      const report = await ensureCompetitorReport(competitorId, selectedMonth)
      if (report?.status === "completed") await fetchDetail(report.id)
      else if (report?.status === "generating") startPolling(report.id, competitorId)
    } else {
      if (existing.status === "completed") await fetchDetail(existing.id)
      else if (existing.status === "generating") startPolling(existing.id, competitorId)
    }
  }

  // ─── 生成ボタン ─────────────────────────────────────────────

  const handleGenerate = async () => {
    const report = currentReport()
    if (!selectedAccountId || !report) return
    setActionLoading(true)
    setErrorMessage(null)
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      await api.patch(`/analytics/ai_reports/${report.id}?${params}`, {})
      startPolling(report.id, activeType === "competitor" ? selectedCompetitorId : null)

      // ステータスを generating に更新
      if (activeType === "competitor" && selectedCompetitorId) {
        setCompReportsByMonth(prev => ({
          ...prev,
          [selectedCompetitorId]: {
            ...(prev[selectedCompetitorId] ?? {}),
            [selectedMonth]: { ...report, status: "generating" }
          }
        }))
      } else {
        const key = `${activeType}:${selectedMonth}`
        setNonCompReportsByMonth(prev => ({ ...prev, [key]: { ...report, status: "generating" } }))
      }
    } catch (err) {
      setErrorMessage((err as ApiError).data?.error ?? "レポートの生成開始に失敗しました")
    } finally {
      setActionLoading(false)
    }
  }

  // ─── fan_user 新規作成 ──────────────────────────────────────

  const handleFanUserCreate = async () => {
    if (!selectedAccountId) return
    setActionLoading(true)
    setErrorMessage(null)
    try {
      const params = new URLSearchParams({ instagram_account_id: String(selectedAccountId) })
      const res = await api.post<AiReportListItem>(`/analytics/ai_reports?${params}`, {
        report_type:  "fan_user",
        target_month: selectedMonth,
      })
      const key = `fan_user:${selectedMonth}`
      setNonCompReportsByMonth(prev => ({ ...prev, [key]: { ...res, status: "generating" } }))
      startPolling(res.id, null)
    } catch (err) {
      setErrorMessage((err as ApiError).data?.error ?? "レポートの生成開始に失敗しました")
    } finally {
      setActionLoading(false)
    }
  }

  // ─── 競合分析 月1回制限チェック ─────────────────────────────

  const competitorAlreadyGeneratedThisMonth = (): boolean => {
    if (activeType !== "competitor") return false
    return Object.entries(compReportsByMonth).some(([cidStr, byMonth]) => {
      if (Number(cidStr) === selectedCompetitorId) return false
      const r = byMonth[selectedMonth]
      return r?.status === "completed" || r?.status === "generating"
    })
  }

  // ─── レンダリング ────────────────────────────────────────────

  const report = currentReport()

  const renderMonthSelector = () => {
    const prevM = canGoPrev ? shiftMonth(selectedMonth, -1) : null
    const nextM = canGoNext ? shiftMonth(selectedMonth, 1) : null

    return (
      <div className="ai-report-month-selector">
        <button
          className="ai-report-month-selector__arrow"
          disabled={!canGoPrev}
          onClick={() => prevM && handleMonthChange(prevM)}
        >
          <i className="bi bi-chevron-left" />
        </button>

        {prevM ? (
          <button
            className="ai-report-month-selector__side"
            onClick={() => handleMonthChange(prevM)}
          >
            {formatMonthLabel(prevM)}
          </button>
        ) : (
          <div className="ai-report-month-selector__side ai-report-month-selector__side--disabled" />
        )}

        <div className="ai-report-month-selector__month">
          {formatMonthLabel(selectedMonth)}
        </div>

        {nextM ? (
          <button
            className="ai-report-month-selector__side"
            onClick={() => handleMonthChange(nextM)}
          >
            {formatMonthLabel(nextM)}
          </button>
        ) : (
          <div className="ai-report-month-selector__side ai-report-month-selector__side--disabled" />
        )}

        <button
          className="ai-report-month-selector__arrow"
          disabled={!canGoNext}
          onClick={() => nextM && handleMonthChange(nextM)}
        >
          <i className="bi bi-chevron-right" />
        </button>
      </div>
    )
  }

  const renderCompetitorSelect = () => {
    if (activeType !== "competitor") return null
    return (
      <div className="ai-report-competitor-select">
        <span className="ai-report-competitor-select__label">競合を選択:</span>
        <select
          className="ai-report-competitor-select__select"
          value={selectedCompetitorId ?? ""}
          onChange={e => handleCompetitorChange(e.target.value ? Number(e.target.value) : null)}
          disabled={competitorsLoading}
        >
          <option value="">-- 競合を選択してください --</option>
          {competitors.map(c => (
            <option key={c.id} value={c.id}>@{c.username}</option>
          ))}
        </select>
      </div>
    )
  }

  const renderGenerateArea = () => {
    // 当月はサンプル表示。生成・再生成ボタンは出さない
    if (isCurrentMonth) return null

    if (!report) {
      if (activeType === "competitor" && !selectedCompetitorId) return null
      if (activeType === "fan_user") {
        return (
          <div className="ai-report__generate">
            <p className="ai-report__desc">
              {formatMonthLabel(selectedMonth)}のAIレポートを生成できます。<br />
              Claudeがデータを分析し、考察と改善提案をまとめます。
            </p>
            {canEdit && (
              <button
                className="ai-report__btn"
                onClick={handleFanUserCreate}
                disabled={actionLoading}
              >
                {actionLoading ? "処理中…" : "生成する"}
              </button>
            )}
          </div>
        )
      }
      return <p className="ai-report__muted">このレポートはまだ作成されていません</p>
    }

    const { status, has_enough_data, regenerable } = report

    if (status === "generating") {
      // 長時間スタックし regenerable が立ったら再生成を促す
      if (regenerable) {
        return (
          <div className="ai-report__generate">
            <p className="ai-report__no-data">
              <i className="bi bi-exclamation-circle" /> 生成に時間がかかっています。再生成をお試しください。
            </p>
            {canEdit && (
              <button className="ai-report__btn" onClick={handleGenerate} disabled={actionLoading}>
                {actionLoading ? "処理中…" : "再生成する"}
              </button>
            )}
          </div>
        )
      }
      return (
        <div className="ai-report__generate">
          <p className="ai-report__generating-label">
            <i className="bi bi-arrow-repeat ai-report__icon-spin" />
            生成中です…しばらくお待ちください
          </p>
        </div>
      )
    }

    if (status === "failed") {
      return (
        <div className="ai-report__generate">
          <p className="ai-report__error">生成に失敗しました。再度お試しください。</p>
          {regenerable && canEdit && (
            <button className="ai-report__btn" onClick={handleGenerate} disabled={actionLoading}>
              {actionLoading ? "処理中…" : "再生成する"}
            </button>
          )}
        </div>
      )
    }

    if (status === "pending") {
      const competitorBlocked = competitorAlreadyGeneratedThisMonth()
      return (
        <div className="ai-report__generate">
          <p className="ai-report__desc">
            {formatMonthLabel(selectedMonth)}のAIレポートを生成できます。<br />
            Claudeがデータを分析し、考察と改善提案をまとめます。
          </p>
          {competitorBlocked && (
            <p className="ai-report__no-data">
              {formatMonthLabel(selectedMonth)}はすでに別の競合のレポートを生成済みです。競合分析は月1回までです。
            </p>
          )}
          {!has_enough_data && (
            <p className="ai-report__no-data">データが不足しているため、レポートを生成できません。</p>
          )}
          {canEdit && (
            <button
              className="ai-report__btn"
              onClick={handleGenerate}
              disabled={actionLoading || !has_enough_data || competitorBlocked}
            >
              {actionLoading ? "処理中…" : "生成する"}
            </button>
          )}
        </div>
      )
    }

    return null
  }

  const renderFollowerTrendChart = (detail: AiReportDetail) => {
    const data = detail.follower_trend_data
    if (!data || data.length === 0) return null

    const labels = data.map(d => {
      const [, , day] = d.date.split("-")
      return parseInt(day) % 5 === 1 ? `${parseInt(day)}日` : ""
    })

    return (
      <div className="ai-report-visual__chart-section">
        <h4>フォロワー推移</h4>
        <div className="ai-report-visual__chart-wrapper">
          <LineChart
            data={{
              labels,
              datasets: [
                {
                  label: `@${selectedAccount?.username ?? "自アカ"}`,
                  data: data.map(d => d.self),
                  borderColor: "#FF9100",
                  backgroundColor: "#FF910020",
                },
                {
                  label: `@${competitors.find(c => c.id === selectedCompetitorId)?.username ?? "競合"}`,
                  data: data.map(d => d.competitor),
                  borderColor: "#6366f1",
                  backgroundColor: "#6366f120",
                },
              ],
            }}
          />
        </div>
      </div>
    )
  }

  const renderComparisonTable = (detail: AiReportDetail) => {
    const table = detail.comparison_table_data
    if (!table) return null

    const selfUsername = selectedAccount?.username ?? "自アカ"
    const compUsername = competitors.find(c => c.id === selectedCompetitorId)?.username ?? "競合"

    type CompRow = {
      label: string
      selfDisplay: string
      compDisplay: string
      selfNum?: number | null
      compNum?: number | null
      higherIsBetter?: boolean
    }

    const rows: CompRow[] = [
      {
        label: "平均ENG率",
        selfDisplay: table.self.avg_engagement_rate != null ? `${table.self.avg_engagement_rate}%` : "-",
        compDisplay: table.competitor.avg_engagement_rate != null ? `${table.competitor.avg_engagement_rate}%` : "-",
        selfNum: table.self.avg_engagement_rate,
        compNum: table.competitor.avg_engagement_rate,
        higherIsBetter: true,
      },
      {
        label: "月間投稿数",
        selfDisplay: `${table.self.posts_count}件`,
        compDisplay: `${table.competitor.posts_count}件`,
        selfNum: table.self.posts_count,
        compNum: table.competitor.posts_count,
        higherIsBetter: true,
      },
      {
        label: "フィード/リール",
        selfDisplay: `${table.self.feed_ratio}% / ${table.self.reel_ratio}%`,
        compDisplay: `${table.competitor.feed_ratio}% / ${table.competitor.reel_ratio}%`,
      },
    ]

    const winnerCls = (selfNum: number | null | undefined, compNum: number | null | undefined, higherIsBetter: boolean | undefined, side: "self" | "comp") => {
      if (selfNum == null || compNum == null || higherIsBetter == null || selfNum === compNum) return ""
      const selfWins = higherIsBetter ? selfNum > compNum : selfNum < compNum
      return (side === "self" ? selfWins : !selfWins) ? "is-winner" : ""
    }

    return (
      <div className="ai-report-visual__comparison">
        <h4>比較サマリー</h4>
        <div className="ai-report-visual__comparison-scroll">
          <table className="ai-report-visual__comparison-table">
            <thead>
              <tr>
                <th>指標</th>
                <th>@{selfUsername}</th>
                <th>@{compUsername}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.label}>
                  <td>{row.label}</td>
                  <td className={winnerCls(row.selfNum, row.compNum, row.higherIsBetter, "self")}>{row.selfDisplay}</td>
                  <td className={winnerCls(row.selfNum, row.compNum, row.higherIsBetter, "comp")}>{row.compDisplay}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderTopPostChip = (post: TopPost | SelfTopPost, index: number, isSelf: boolean) => {
    const isTopPost = !isSelf
    const tp = post as TopPost
    const sp = post as SelfTopPost

    const rank = isTopPost ? tp.rank : index + 1
    const caption = post.caption
    const engRate = isTopPost ? tp.engagement_rate : sp.engagement_rate
    const likes = isTopPost ? tp.likes : null
    const comments = isTopPost ? tp.comments : null
    const permalink = isTopPost ? tp.permalink : null
    const thumbUrl = isTopPost ? tp.thumbnail_url : null

    const chip = (
      <div className="ai-report-visual__post-chip">
        <div className="ai-report-visual__post-thumb">
          {thumbUrl
            ? <img src={thumbUrl} alt="" onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} />
            : <i className="bi bi-image" />
          }
        </div>
        <div className="ai-report-visual__post-info">
          <div className="ai-report-visual__post-rank">#{rank}</div>
          <div className="ai-report-visual__post-caption">{caption}</div>
          <div className="ai-report-visual__post-stats">
            {engRate != null && <span><i className="bi bi-graph-up" /> ENG {engRate}%</span>}
            {likes != null && <span><i className="bi bi-heart" /> {likes}</span>}
            {comments != null && <span><i className="bi bi-chat" /> {comments}</span>}
            {isSelf && <span><i className="bi bi-bookmark" /> {sp.saves}</span>}
          </div>
        </div>
      </div>
    )

    if (permalink) {
      return (
        <a key={index} href={permalink} target="_blank" rel="noreferrer" className="ai-report-visual__post-chip-link">
          {chip}
        </a>
      )
    }
    return <div key={index}>{chip}</div>
  }

  const renderTopPosts = (detail: AiReportDetail) => {
    const compPosts = detail.competitor_top_posts ?? []
    const selfPosts = detail.self_top_posts ?? []
    if (compPosts.length === 0 && selfPosts.length === 0) return null

    const compUsername = competitors.find(c => c.id === selectedCompetitorId)?.username ?? "競合"
    const selfUsername = selectedAccount?.username ?? "自アカ"

    return (
      <div className="ai-report-visual__top-posts">
        <h4>トップ投稿比較</h4>
        <div className="ai-report-visual__top-posts-grid">
          <div className="ai-report-visual__top-posts-col">
            <h5>@{compUsername}</h5>
            <div className="ai-report-visual__post-chips">
              {compPosts.map((post, i) => renderTopPostChip(post, i, false))}
            </div>
          </div>
          <div className="ai-report-visual__top-posts-col">
            <h5>@{selfUsername}</h5>
            <div className="ai-report-visual__post-chips">
              {selfPosts.map((post, i) => renderTopPostChip(post, i, true))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const avatarColor = (ownerIgId: string): string => {
    const colors = ["#FF9100", "#6366f1", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#06b6d4", "#84cc16"]
    let hash = 0
    for (let i = 0; i < ownerIgId.length; i++) hash = (hash * 31 + ownerIgId.charCodeAt(i)) | 0
    return colors[Math.abs(hash) % colors.length]
  }

  const renderAmbassadorRanking = (detail: AiReportDetail) => {
    const items: AmbassadorRankingItem[] = detail.ambassador_ranking ?? []
    return (
      <div className="ai-report-fan-section analytics__section">
        <h4 className="ai-report-fan-section__title">アンバサダーランキング（当月エンゲージメント上位5名）</h4>
        {items.length === 0 ? (
          <p className="ai-report__muted">当月のUGCデータがありません</p>
        ) : (
          <div className="ai-report-fan-ranking">
            {items.map(item => (
              <div key={item.owner_ig_id} className="ai-report-fan-ranking__card">
                <div className="ai-report-fan-ranking__avatar-wrap">
                  <div
                    className="ai-report-fan-ranking__avatar"
                    style={{ backgroundColor: avatarColor(item.owner_ig_id) }}
                  >
                    {item.display_name.slice(0, 2).replace("@", "").toUpperCase().slice(0, 2)}
                  </div>
                  <div className="ai-report-fan-ranking__badge">#{item.rank}</div>
                </div>
                <div className="ai-report-fan-ranking__name">{item.display_name}</div>
                <div className="ai-report-fan-ranking__posts">{item.posts_count}件投稿</div>
                <div className="ai-report-fan-ranking__stats">
                  <span><i className="bi bi-heart" /> {item.total_likes.toLocaleString()}</span>
                  <span><i className="bi bi-chat" /> {item.total_comments.toLocaleString()}</span>
                </div>
                <a
                  href={`/fan-users/${item.owner_ig_id}?account_id=${selectedAccountId}`}
                  className="ai-report-fan-ranking__link"
                >
                  詳しく見る <i className="bi bi-arrow-right" />
                </a>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  const renderUgcComparison = (detail: AiReportDetail) => {
    const cmp = detail.ugc_comparison ?? {}
    const curr: Partial<UgcComparisonSide> = cmp.current ?? {}
    const prev: Partial<UgcComparisonSide> = cmp.prev ?? {}
    if (!cmp.current && !cmp.prev) return null

    const rows: { label: string; curr: number | undefined; prev: number | undefined }[] = [
      { label: "投稿総数",     curr: curr.total,     prev: prev.total },
      { label: "タグ付け数",   curr: curr.tagged,    prev: prev.tagged },
      { label: "メンション数", curr: curr.mentioned, prev: prev.mentioned },
      { label: "いいね数",     curr: curr.likes,     prev: prev.likes },
      { label: "コメント数",   curr: curr.comments,  prev: prev.comments },
    ]

    return (
      <div className="ai-report-fan-section analytics__section">
        <h4 className="ai-report-fan-section__title">UGCサマリー比較（当月 vs 先月）</h4>
        <div className="ai-report-visual__comparison-scroll">
          <table className="ai-report-visual__comparison-table">
            <thead>
              <tr>
                <th>指標</th>
                <th>当月</th>
                <th>先月</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => {
                const c = row.curr ?? 0
                const p = row.prev ?? 0
                return (
                  <tr key={row.label}>
                    <td>{row.label}</td>
                    <td className={c > p ? "is-winner" : ""}>{c.toLocaleString()}</td>
                    <td className={p > c ? "is-winner" : ""}>{p.toLocaleString()}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>
    )
  }

  const renderUgcPostChip = (post: TopUgcPost) => {
    const chip = (
      <div className="ai-report-visual__post-chip">
        <div className="ai-report-visual__post-thumb">
          {post.media_url
            ? <img src={post.media_url} alt="" onError={(e) => { (e.target as HTMLImageElement).style.display = "none" }} />
            : <i className="bi bi-image" />
          }
        </div>
        <div className="ai-report-visual__post-info">
          <div className="ai-report-visual__post-rank">#{post.rank}</div>
          <div className="ai-report-visual__post-caption">{post.display_name}</div>
          <div className="ai-report-visual__post-stats">
            <span><i className="bi bi-heart" /> {post.like_count}</span>
            <span><i className="bi bi-chat" /> {post.comments_count}</span>
            <span>{post.tagged_at}</span>
          </div>
        </div>
      </div>
    )
    if (post.permalink) {
      return (
        <a key={post.rank} href={post.permalink} target="_blank" rel="noreferrer" className="ai-report-visual__post-chip-link">
          {chip}
        </a>
      )
    }
    return <div key={post.rank}>{chip}</div>
  }

  const renderTopUgcPosts = (detail: AiReportDetail) => {
    const curr: TopUgcPost[] = detail.top_ugc_posts_current ?? []
    const prev: TopUgcPost[] = detail.top_ugc_posts_prev ?? []
    if (curr.length === 0 && prev.length === 0) return null

    return (
      <div className="ai-report-fan-section analytics__section">
        <h4 className="ai-report-fan-section__title">トップ投稿比較</h4>
        <div className="ai-report-visual__top-posts-grid">
          <div className="ai-report-visual__top-posts-col">
            <h5>当月</h5>
            <div className="ai-report-visual__post-chips">
              {curr.length > 0 ? curr.map(p => renderUgcPostChip(p)) : <p className="ai-report__muted">データなし</p>}
            </div>
          </div>
          <div className="ai-report-visual__top-posts-col">
            <h5>先月</h5>
            <div className="ai-report-visual__post-chips">
              {prev.length > 0 ? prev.map(p => renderUgcPostChip(p)) : <p className="ai-report__muted">データなし</p>}
            </div>
          </div>
        </div>
      </div>
    )
  }

  const renderFanUserVisual = (detail: AiReportDetail) => {
    if (detail.report_type !== "fan_user") return null
    return (
      <>
        {renderAmbassadorRanking(detail)}
        {renderUgcComparison(detail)}
        {renderTopUgcPosts(detail)}
      </>
    )
  }

  const renderFunnelFlowChart = (detail: AiReportDetail) => {
    if (detail.report_type !== "funnel" || !detail.chart_data) return null
    return (
      <div className="ai-report-funnel-chart analytics__section">
        <FunnelFlowChart data={detail.chart_data} />
      </div>
    )
  }

  const renderVisualComponents = (detail: AiReportDetail) => {
    if (detail.report_type !== "competitor") return null
    return (
      <div className="ai-report-visual analytics__section">
        {renderFollowerTrendChart(detail)}
        {renderComparisonTable(detail)}
        {renderTopPosts(detail)}
      </div>
    )
  }

  const renderDetail = (detail: AiReportDetail) => {
    const content: AiReportContent | null = (() => {
      if (!detail.content) return null
      try { return JSON.parse(detail.content) } catch { return null }
    })()

    return (
      <>
        {renderFunnelFlowChart(detail)}
        {renderFanUserVisual(detail)}
        {renderVisualComponents(detail)}
        {content ? (
          <div className="ai-report__content">
            {content.sections.map((section, i) => (
              <div key={i} className="ai-report__section">
                <h3 className="ai-report__section-title">{section.title}</h3>
                <div className="ai-report__section-body">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>{section.content}</ReactMarkdown>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="ai-report__muted">レポートの表示に失敗しました</p>
        )}
      </>
    )
  }

  const renderReportContent = () => {
    if (!selectedReport || selectedReport.status !== "completed") return null
    return renderDetail(selectedReport)
  }

  // 当月選択時のサンプル表示（バッジ＋案内帯＋静的モック）
  const renderSampleReport = () => (
    <>
      <div className="ai-report__sample-head">
        <span className="ai-report__sample-badge">
          <i className="bi bi-stars" /> サンプル
        </span>
      </div>
      <div className="ai-report__sample-notice">
        <i className="bi bi-info-circle" />
        <span>
          {formatMonthLabel(selectedMonth)}のレポートは翌月になると生成できます。それまでサンプルレポートをご覧ください。
        </span>
      </div>
      {renderDetail(sampleReport)}
    </>
  )

  return (
    <AppLayout pageTitle={PAGE_TITLES["/ai-report"]}>
      <div className="analytics">
        {!selectedAccountId ? (
          <AccountSelectionNotice className="ai-report__muted" />
        ) : locked ? (
          <PlanLockNotice message={`${PAGE_TITLES["/ai-report"]}はプロプラン以上でご利用いただけます`} />
        ) : loading ? (
          <p className="ai-report__muted">読み込み中…</p>
        ) : (
          <>
            {/* レポートタイプ切り替えタブ */}
            <div className="filter-tabs ai-report__tabs-wrap">
              {REPORT_TYPE_OPTIONS.map(({ key, label }) => (
                <button
                  key={key}
                  className={`filter-tabs__item${activeType === key ? " filter-tabs__item--active" : ""}`}
                  onClick={() => handleTabChange(key)}
                >
                  {label}
                </button>
              ))}
            </div>

            {/* 月選択 */}
            {renderMonthSelector()}

            {/* 競合選択（競合分析タブのみ） */}
            {renderCompetitorSelect()}

            {errorMessage && (
              <p className="ai-report__error ai-report__error-bar">{errorMessage}</p>
            )}

            {isCurrentMonth ? (
              /* 当月: サンプル表示 */
              renderSampleReport()
            ) : (
              <>
                {/* 生成エリア */}
                {renderGenerateArea()}

                {/* レポート本文 */}
                {detailLoading ? (
                  <p className="ai-report__muted">読み込み中…</p>
                ) : renderReportContent()}
              </>
            )}
          </>
        )}
      </div>
    </AppLayout>
  )
}

export default AiReportPage
